// src/iap.ts
import {
  Purchases,
  type PurchasesPackage,
} from "@revenuecat/purchases-capacitor";
import { useSyncExternalStore } from 'react';
import { Capacitor } from '@capacitor/core';
import { recordGoogleSub } from "./useServerSubscription"; 
import { getAccessToken } from "./api";

// -------------------- constants/exports --------------------
export const MONTHLY_PKG    = '$rc_monthly';
export const ENTITLEMENT_ID = 'smart_timing';
export const isNative = () => Capacitor.isNativePlatform();

// -------------------- small utils --------------------
const extractCI = (x: any) => (x && 'customerInfo' in x ? x.customerInfo : x);

// -------------------- subscription tick store --------------------
let subTick = 0;
const listeners = new Set<() => void>();
const notify = () => { subTick++; listeners.forEach(fn => fn()); };
export const useSubTick = () =>
  useSyncExternalStore(
    (cb) => {
      listeners.add(cb);

      return () => {
        listeners.delete(cb);
      };
    },
    () => subTick,
    () => subTick
  );

// -------------------- entitlement state --------------------
let smartTimingEnabled = false;
export const hasSmartTiming = () => smartTimingEnabled;

// -------------------- idempotency guards --------------------
let configured = false;
let listenersAttached = false;


// --- sync RC entitlement → backend (throttled, native-only, resilient) -----
async function syncSubscriptionWithBackendFromCI(
  _ci: any
) {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const bearer = getAccessToken();

  if (!bearer) {
    throw new Error(
      "[IAP] Cannot sync subscription without an access token"
    );
  }

  const response = await fetch(
    `${
      import.meta.env.VITE_API_BASE_URL ||
      "https://api.satstacker.app"
    }/api/subscriptions/revenuecat/sync`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${bearer}`,
        Accept: "application/json",
      },
    }
  );

  if (!response.ok) {
    const body =
      await response.text().catch(() => "");

    throw new Error(
      "[IAP] RevenueCat subscription sync failed: " +
        `${response.status} ${body}`
    );
  }
}

// -------------------- public: manual backend sync by purchase token ----------
export async function syncSubToBackend(): Promise<void> {
  return forceSyncFromRevenueCat();
}

export async function forceSyncFromRevenueCat(): Promise<void> {
  const info = await Purchases.getCustomerInfo();
  const ci = (info && 'customerInfo' in info) ? (info as any).customerInfo : info;
  await syncSubscriptionWithBackendFromCI(ci);
}

async function acknowledgePurchase(purchaseToken: string): Promise<void> {
  // e.g., await MyBillingPlugin.acknowledge({ purchaseToken })
  // throw new Error if the bridge returns an error
}

async function queryPurchasesAsync(): Promise<Array<{ purchaseToken: string; acknowledged: boolean }>> {
  // e.g., const { purchases } = await MyBillingPlugin.queryActiveSubscriptions()
  // return purchases
  return [];
}

async function onAndroidPurchaseApproved(purchase: {
  purchaseToken: string;
  acknowledged: boolean;
}) {
  try {
    // 1) Acknowledge (required so Play doesn’t auto-refund)
    if (!purchase.acknowledged) {
      await acknowledgePurchase(purchase.purchaseToken); // <- your existing ack call
    }

    // 2) Tell your server so it can flip is_premium
    //    This will use the Bearer placed in localStorage by Pricing.jsx before purchase.
    await recordGoogleSub(purchase.purchaseToken);

    // (optional) notify UI
    // window.dispatchEvent(new CustomEvent("iap:server-confirmed"));

  } catch (err) {
    console.error("[Android IAP] post-purchase server record failed:", err);
    // You can surface: “Tap Restore if not upgraded in 30s”
  }
}

export async function restoreAndroidSubscriptions() {
  try {
    const purchases = await queryPurchasesAsync(); // BillingClient query for ACTIVE subs
    for (const p of purchases) {
      if (p.purchaseToken) {
        await recordGoogleSub(p.purchaseToken);
      }
    }
  } catch (err) {
    console.error("[Android IAP] restore failed:", err);
  }
}

// -------------------- init + listeners (idempotent) -------------------------
export async function initIAP() {
  if (!isNative()) return;
  if (configured) return;

  // RevenueCat is configured centrally by App.jsx.
  // Wait for that shared configuration instead of configuring again.
  const configPromise = window.__RC_CONFIG_PROMISE__;

  if (!configPromise) {
    throw new Error(
      "[IAP] RevenueCat configuration has not started"
    );
  }

  await configPromise;

  const platform = Capacitor.getPlatform();

  console.log(
    "[IAP] attaching listeners for platform:",
    platform
  );

  // Attach listeners once
  if (!listenersAttached) {
    Purchases.addCustomerInfoUpdateListener(async (payload: any) => {
      const ci = extractCI(payload);
      console.log('[IAP] customerInfo update:', JSON.stringify(ci?.entitlements, null, 2));

      const active =
        !!ci?.entitlements?.active?.[ENTITLEMENT_ID] ||
        !!ci?.entitlements?.active?.['Smart Timing'];

      if (active !== smartTimingEnabled) {
        smartTimingEnabled = active;
        notify();
      }

      if (Capacitor.getPlatform() === 'android') {
        try { await fetch(`${import.meta.env.VITE_API_BASE_URL || "https://api.satstacker.app"}/me/subscription`, {
          headers: {
            Authorization: `Bearer ${getAccessToken() || ""}`
          }
        }); } catch {}
      }
      await syncSubscriptionWithBackendFromCI(ci);

      configured = true;
    });
    listenersAttached = true;
  }

  // Seed initial state + sync
  const info = await Purchases.getCustomerInfo();
  const ci = extractCI(info);
  console.log('[IAP] initial customerInfo:', JSON.stringify(ci?.entitlements, null, 2));

  smartTimingEnabled =
    !!ci?.entitlements?.active?.[ENTITLEMENT_ID] ||
    !!ci?.entitlements?.active?.['Smart Timing'];

  notify();
  await syncSubscriptionWithBackendFromCI(ci);
}

// -------------------- purchase flow -----------------------------------------
export async function buySmartTiming() {
  if (!isNative()) return;

  try {
    // 1) Try offerings first
    const offerings = await Purchases.getOfferings();
    console.log('[IAP] offerings:', offerings);

    const current =
      offerings.current ?? Object.values(offerings.all ?? {})[0];
    console.log('[IAP] selected offering:', current);

    if (current?.availablePackages?.length) {
      const ids = current.availablePackages.map(p => p.identifier);
      console.log('[IAP] available package IDs:', ids);

      const pkg: PurchasesPackage | undefined =
        current.availablePackages.find(p => p.identifier === MONTHLY_PKG)
        ?? current.availablePackages[0];

      if (!pkg) throw new Error('No packages available in offering');

      console.log('[IAP] purchasing package:', pkg.identifier);
      const result = await Purchases.purchasePackage({ aPackage: pkg });
      console.log('[IAP] purchasePackage result:', JSON.stringify(result, null, 2));

      const ci = extractCI(result);

      smartTimingEnabled =
        !!ci?.entitlements?.active?.[ENTITLEMENT_ID] ||
        !!ci?.entitlements?.active?.["Smart Timing"];

      notify();

      try {
        await syncSubscriptionWithBackendFromCI(ci);
      } catch (syncError) {
        console.warn(
          "[IAP] purchase succeeded, but immediate backend sync failed; relying on RevenueCat webhook",
          syncError
        );
      }

      return result;
    }

    // 2) Fallback: direct product purchase (platform-aware ID)
    const platform = Capacitor.getPlatform();
    const PRODUCT_ID =
      platform === 'android'
        ? 'smart_timing_monthly:smart-timing'
        : 'smart_timing_monthly';

    console.log('[IAP] fallback getProducts for:', PRODUCT_ID);
    const { products } = await Purchases.getProducts({
      productIdentifiers: [PRODUCT_ID],
    });
    console.log('[IAP] getProducts returned:', products?.map(p => p.identifier));

    const product =
      products?.find(p => p.identifier === PRODUCT_ID) ?? products?.[0];
    if (!product) throw new Error('No products available from getProducts');

    console.log('[IAP] purchasing store product:', product.identifier);
    const res = await Purchases.purchaseStoreProduct({ product });
    console.log('[IAP] purchaseStoreProduct result:', JSON.stringify(res, null, 2));

    const ci = extractCI(res);
    smartTimingEnabled =
      !!ci?.entitlements?.active?.[ENTITLEMENT_ID] ||
      !!ci?.entitlements?.active?.['Smart Timing'];
    notify();

    try {
      await syncSubscriptionWithBackendFromCI(ci);
    } catch (syncError) {
      console.warn(
        "[IAP] purchase succeeded, but immediate backend sync failed; relying on RevenueCat webhook",
        syncError
      );
    }

    return res;

  } catch (e: any) {
    console.error('[IAP] buySmartTiming failed:', e, JSON.stringify(e, null, 2));
    alert(e?.message ?? 'Purchase failed. Please try again.');
    throw e;
  }
}

// -------------------- restore -----------------------------------------------
export async function restorePurchases() {
  if (!isNative()) return;
  const info = await Purchases.restorePurchases();
  const ci   = extractCI(info);
  smartTimingEnabled =
    !!ci?.entitlements?.active?.[ENTITLEMENT_ID] ||
    !!ci?.entitlements?.active?.['Smart Timing'];
  notify();
  await syncSubscriptionWithBackendFromCI(ci);
}
